import type { ReactNode } from 'react'
import type { PressEventHandler } from '@/infra/prop-types'
import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import {
  ScreenHeaderContent,
  ScreenHeaderMain,
  ScreenHeaderPersistentTopBar,
  ScreenHeaderPersistentTopBarAction,
  ScreenHeaderPersistentTopBarLayout,
  ScreenHeaderPersistentTopBarTitle,
  ScreenHeaderProvider,
  ScreenHeaderTitle,
  useScreenHeaderTotalOffset,
} from '@/components/ScreenHeader'
import { IconButton } from '@/components/IconButton'
import { useIsSpacious } from '@/hooks/viewport-hooks'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { type ActionCard } from '@/features/jobs/details/components/ActionCard'
import { links } from '@/utils/link'
import { Tab, TabList } from '@/components/Tabs'
import { IsSkeletonContext, Skeleton } from '@/components/Skeleton'
import { Badge } from '@/components/Badge'
import {
  Breadcrumbs,
  BreadcrumbItem,
} from '@/features/jobs/details/components/Breadcrumbs'
import type { JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { dateFormatters } from '@/utils/date-util'
import {
  getStatusDisplayDescription,
  getStatusDisplayHeadline,
  type GetStatusDisplayTextOptions,
} from '@/features/jobs/job-status-display'
import {
  PendingJobScopeChangeScrolledActionContainer,
  PendingJobScopeChangeSectionContainer,
} from '@/features/jobs/details/components/PendingJobScopeChangeSection'
import {
  RateJobActionCard,
  RateJobScrolledAction,
} from '@/features/jobs/details/ratings/components/RatingsJobDetailsHeader'
import { useTranslate } from '@/hooks/translate'
import { usePageExitMethod } from '@/features/shell/exiting/exit-method-hooks'
import styles from './PageHeader.module.css'

export interface JobDetailsHeaderProps {
  /**
   * Job details view model.
   */
  vm: JobDetailsViewModel
  /**
   * Primary headline. Will also be the scrolled headline if none is set.
   */
  headline: string
  /**
   * Description underneath the headline.
   */
  description: ReactNode
  /**
   * Optional {@link ActionCard}.
   */
  actionCard?: ReactNode
  /**
   * Overrides the {@link headline} if specified.
   */
  scrolledHeadline?: string
  /**
   * Action that goes underneath the scrolled headline.
   * Should be a {@link ScrolledAction} component.
   */
  scrolledAction?: ReactNode

  /**
   * Whether we're rendering a skeleton of the header.
   */
  isSkeleton?: boolean
}

/**
 * Header for the job details page.
 */
export const PageHeader = observer(function PageHeader({
  vm,
  headline,
  description,
  actionCard,
  scrolledHeadline,
  scrolledAction,
  isSkeleton,
}: JobDetailsHeaderProps) {
  const isSpacious = useIsSpacious()
  const { isExitMethodVisible, exitMethodProps } = usePageExitMethod()

  return (
    <IsSkeletonContext value={isSkeleton}>
      <ScreenHeaderProvider isOverlay={isSpacious} applyDocumentScrollPadding>
        <ScreenHeaderPersistentTopBar fadeWhen="title-exit">
          <ScreenHeaderPersistentTopBarLayout
            visibility={isSpacious ? 'title-exit' : 'always'}
            left={
              !isSpacious &&
              isExitMethodVisible && (
                <IconButton
                  name="back"
                  isSkeleton={false}
                  label="Back"
                  {...exitMethodProps}
                />
              )
            }
          >
            <ScreenHeaderPersistentTopBarTitle
              visibility="content-exit"
              action={scrolledAction}
            >
              {isSkeleton ? <Skeleton width={120} /> : scrolledHeadline}
            </ScreenHeaderPersistentTopBarTitle>
          </ScreenHeaderPersistentTopBarLayout>
        </ScreenHeaderPersistentTopBar>
        <ScreenHeaderMain>
          <ScreenHeaderContent>
            <div className={styles.headerContainer}>
              <div className={styles.headerLayout}>
                <header className={styles.header}>
                  <ScreenHeaderTitle>
                    {isSpacious && (
                      <Breadcrumbs>
                        {isExitMethodVisible && (
                          <BreadcrumbItem to={links.jobs()} isSkeleton={false}>
                            Jobs
                          </BreadcrumbItem>
                        )}

                        <BreadcrumbItem isSkeleton={false}>
                          {vm.job.title}
                        </BreadcrumbItem>
                      </Breadcrumbs>
                    )}

                    <h1 className={cx('text-display-lg')}>
                      {isSkeleton ? <Skeleton width="60%" /> : headline}
                    </h1>
                  </ScreenHeaderTitle>
                  <div className="text-body-md-light mt-md">
                    {isSkeleton ? (
                      <>
                        <Skeleton width="80%" />
                        <Skeleton width="40%" />
                      </>
                    ) : (
                      description
                    )}
                  </div>
                </header>
                {actionCard && (
                  <div className={styles.actionCardContainer}>{actionCard}</div>
                )}
              </div>
            </div>
          </ScreenHeaderContent>
        </ScreenHeaderMain>
        <PageTabs vm={vm} />
      </ScreenHeaderProvider>
    </IsSkeletonContext>
  )
})

interface ScrolledActionProps {
  onPress?: PressEventHandler
  label: string
  isDisabled?: boolean
  isSkeleton?: boolean
  testId?: string
}

/**
 * Scrolled Action
 */
export const ScrolledAction = observer(function ScrolledAction({
  label,
  onPress,
  isDisabled,
  isSkeleton,
  testId,
}: ScrolledActionProps) {
  return (
    <ScreenHeaderPersistentTopBarAction
      onPress={onPress}
      label={label}
      isSkeleton={isSkeleton}
      isDisabled={isDisabled}
      testId={testId}
    />
  )
})

interface PageTabsProps {
  vm: JobDetailsViewModel
}

/**
 * Page Tabs
 */
const PageTabs = observer(function PageTabs({ vm }: PageTabsProps) {
  const isSpacious = useIsSpacious()
  const top = useScreenHeaderTotalOffset()

  return (
    <div
      className={cx(styles.tabs, isSpacious ? 'px-6xl' : 'px-3xl')}
      style={{ top }}
    >
      <div className={'mx-auto max-w-content'}>
        <TabList>
          <Tab
            label="Overview"
            replace
            to="./"
            testId="job-details-tab-overview"
          />
          <Tab
            label="Messages"
            replace
            to="./messages"
            testId="job-details-tab-messages"
          >
            <Badge hidden={vm.conversationVm.unreadCount === 0}>
              {vm.conversationVm.unreadCount > 99
                ? '99+'
                : vm.conversationVm.unreadCount}
            </Badge>
          </Tab>
          <Tab
            label="Documents"
            replace
            to="./docs"
            testId="job-details-tab-documents"
          />
          {vm.isBillingTabEnabled && (
            <Tab
              label="Billing"
              replace
              to="./billing"
              testId="job-details-tab-billing"
            >
              <Badge
                hidden={!vm.jobScopeChangesDisplayVm.pendingJobScopeChange}
              >
                !
              </Badge>
            </Tab>
          )}
        </TabList>
      </div>
    </div>
  )
})

/**
 * Gets the default header content for a closed job.
 *
 * @param jobVm
 */
export function useJobClosedHeaderContent(jobVm: JobDetailsViewModel) {
  const T = useTranslate()
  const workspaceName = T('Web.Common.WorkspaceName', jobVm.job.workspace.name)
  const title = `Thank you for choosing ${workspaceName}`
  const description = `We're delighted to have been of service to you. Your request was completed on ${dateFormatters.dateTimeFull.format(jobVm.job.closedAt)}.`

  return { title, description }
}

/**
 * Gets the default header content for a job.
 *
 * @param jobVm
 * @param options
 */
export function useDefaultHeaderContent(
  jobVm: JobDetailsViewModel,
  options?: Partial<GetStatusDisplayTextOptions>,
) {
  const closedContent = useJobClosedHeaderContent(jobVm)

  if (jobVm.job.status === 'Closed') {
    return closedContent
  }

  return {
    title: getStatusDisplayHeadline({ ...options, status: jobVm.job.status }),
    description: getStatusDisplayDescription({
      ...options,
      status: jobVm.job.status,
    }),
  }
}

/**
 * If there's a pending job scope change, returns action card and scrolled
 * action elements to interact with it.
 *
 * @param jobVm
 */
export function getPendingJobScopeChangeElements(jobVm: JobDetailsViewModel): {
  actionCard: ReactNode
  scrolledAction: ReactNode
} | null {
  const pendingJobScopeChange =
    jobVm.jobScopeChangesDisplayVm.pendingJobScopeChange

  if (!pendingJobScopeChange) {
    return null
  }

  return {
    actionCard: (
      <PendingJobScopeChangeSectionContainer
        vm={jobVm.jobScopeChangesDisplayVm}
      />
    ),
    scrolledAction: (
      <PendingJobScopeChangeScrolledActionContainer
        vm={jobVm.jobScopeChangesDisplayVm}
      />
    ),
  }
}

/**
 * If the job is rateable, returns action card and scrolled action elements
 * for rating the job with generic messaging.
 *
 * @param jobVm
 */
export function getRatingsElements(jobVm: JobDetailsViewModel): {
  actionCard: ReactNode
  scrolledAction: ReactNode
} | null {
  if (!jobVm.ratingsVm.isRateable) {
    return null
  }

  return {
    actionCard: (
      <RateJobActionCard
        kicker="How did we do?"
        title="Let us know"
        onRatePress={jobVm.ratingsVm.showRatingModal}
        testId="job-details-header-leave-review-button"
      />
    ),
    scrolledAction: (
      <RateJobScrolledAction onRatePress={jobVm.ratingsVm.showRatingModal} />
    ),
  }
}
